<template>
    <div v-redirect-if-no-permission:SERVICES>
        <div class="text-xs-left" style="margin-bottom: 8px">
            <v-btn flat color="primary" :to="{ name: 'CameraStreamLogs' }">
                <v-icon left>history</v-icon>
                {{ $t("camerasPage.streamLogsButton") }}
            </v-btn>
        </div>
        <DataTable ref="datatable" :url="api.ACTIONS.CAMERAS" :initial-url-search-params="cameraParams"
            :fieldsHiddenMain='["updated_at", "notes", "car_ids"]'
            :fieldsHiddenAdd='["id", "created_at", "updated_at", "cars", "online", "last_seen", "provider"]'
            :fieldsHiddenEdit='["id", "created_at", "updated_at", "cars", "online", "last_seen", "provider"]'
            :formatter="formatter" :fields-extra-main="extraMain" :actionsExtraMain="actionsExtraMain"
            :conditional-action="conditionalAction"
            :fields-extra-add="extraEditor" :fields-extra-edit="extraEditor"
            :add-rules="rules(true)" :edit-rules="rules(false)" :forceReload="true"
            :titles="titles" :title='$t("camerasPage.pageTitle")' />
    </div>
</template>

<script>

import DataTable from "@/components/monaco/DataTable";
import { formatCompanyTime } from "@/util/companyTime";

export default {
    components: { DataTable },
    name: "Cameras",
    title() {
        return this.$t("camerasPage.pageTitle");
    },
    computed: {
        cameraParams() {
            // with_status merges online/last_seen from the camera cloud into
            // the index rows (backend degrades to null if the vendor is down).
            // Passed as initial-url-search-params — never baked into the url
            // prop, which DataTable also uses to build edit/delete URLs.
            return new URLSearchParams({ with_status: "1" });
        },
        // car/online/last_seen are backend-computed display fields: route them
        // through fields-extra-main so their columns render without bogus sort
        // arrows (extra fields are created non-sortable).
        extraMain() {
            return {
                cars: (item) => item.cars,
                online: (item) => item.online,
                last_seen: (item) => item.last_seen,
            }
        },
        extraEditor() {
            return {
                imei: 'v-text-field',
                name: 'v-text-field',
                car_ids: 'choose-car-multi',
                notes: 'v-text-field',
            }
        },
        formatter() {
            const vm = this;
            // Cells render through v-html — escape every user-controlled value.
            const esc = s => String(s == null ? "" : s)
                .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
            return {
                // One physical vehicle is one `cars` row per captain who
                // drives it, and the dashcam serves all of them — so this is a
                // list, and the plate repeating across lines is expected.
                cars(value) {
                    if (!value || !value.length) return `<span style="color: grey">${vm.$t("camerasPage.unbound")}</span>`;
                    return value.map(car => {
                        const captain = car.captain ? ` — ${esc(car.captain.first_name)} ${esc(car.captain.last_name)}` : "";
                        return `${esc(car.plate_number)}${captain}`;
                    }).join("<br>");
                },
                online(value) {
                    if (value === null || value === undefined)
                        return `<span style="color: grey">${vm.$t("camerasPage.statusUnknown")}</span>`;
                    return value
                        ? `<span style="color: green">${vm.$t("camerasPage.statusOnline")}</span>`
                        : `<span style="color: red">${vm.$t("camerasPage.statusOffline")}</span>`;
                },
                created_at(value) {
                    return formatCompanyTime(value, "YYYY-MM-DD");
                },
            }
        },
        titles() {
            return {
                "id": "#",
                "imei": this.$t("camerasPage.colImei"),
                "name": this.$t("camerasPage.colName"),
                "provider": this.$t("camerasPage.colProvider"),
                "cars": this.$t("camerasPage.colCars"),
                "car_ids": this.$t("camerasPage.colCars"),
                "online": this.$t("camerasPage.colStatus"),
                "last_seen": this.$t("camerasPage.colLastSeen"),
                "notes": this.$t("camerasPage.colNotes"),
                "created_at": this.$t("camerasPage.colDateCreated"),
            }
        },
        conditionalAction() {
            return {
                unbind(item) {
                    return !!(item.cars && item.cars.length);
                }
            }
        },
        actionsExtraMain() {
            return {
                live: {
                    icon: 'videocam',
                    extras: { color: 'green' },
                    action: (item) => {
                        // Open the tab synchronously (inside the click) so the
                        // popup blocker doesn't eat it, then point it at the
                        // stream URL when the backend responds.
                        const win = window.open("", "_blank");
                        this.axios
                            .get(this.api.ACTIONS.CAMERA_LIVE_URL(item.id))
                            .then((res) => {
                                const url = res.data.data && res.data.data.url;
                                if (url && win) {
                                    win.location = url;
                                } else {
                                    if (win) win.close();
                                    window.getApp.$emit("SHOW_TOAST", this.$t("camerasPage.liveUrlMissing"), "red");
                                }
                            })
                            .catch((err) => {
                                if (win) win.close();
                                window.getApp.$emit("SHOW_TOAST", (err.response && err.response.data.message) || this.$t("camerasPage.liveUrlFailed"), "red");
                            });
                    }
                },
                unbind: {
                    icon: 'link_off',
                    extras: { color: 'orange' },
                    action: (item) => {
                        this.$dialova({
                            title: this.$t("camerasPage.unbindTitle"),
                            paragraph: this.$t("camerasPage.unbindConfirm", { imei: item.imei }),
                            loader: true,
                        })
                            .then(({ close }) => {
                                this.axios
                                    .put(`camera/` + item.id, { car_ids: [] })
                                    .then(() => {
                                        window.getApp.$emit("SHOW_TOAST", this.$t("camerasPage.unbindDone"), "green");
                                        this.$refs['datatable'].getData();
                                        close();
                                    })
                                    .catch((err) => {
                                        window.getApp.$emit("SHOW_TOAST", (err.response && err.response.data.message) || this.$t("camerasPage.errorOccurred"), "red");
                                        close();
                                    });
                            })
                            .catch(() => { });
                    }
                },
            }
        }
    },
    methods: {
        rules(add) {
            const required = value => !!value || this.$t("camerasPage.ruleRequired");
            const imei = value => {
                if (!value) return true;
                return /^\d{8,20}$/.test(value) || this.$t("camerasPage.ruleImei");
            };
            return {
                imei: add ? [required, imei] : [imei],
            }
        },
    },
}
</script>
